import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { systemLocale, type Locale } from '../l10n/messages';

// This browser's preferences: look and language, as the mobile app keeps them on the device. They are
// conveniences, so a browser that refuses storage simply follows the system.

export type ThemeMode = 'system' | 'light' | 'dark';
export type LocaleChoice = 'system' | Locale;

interface Prefs {
  theme: ThemeMode;
  localeChoice: LocaleChoice;
  /** The language in use: the choice, or the browser's when following the system. */
  locale: Locale;
  setTheme: (t: ThemeMode) => void;
  setLocale: (l: LocaleChoice) => void;
}

/** Where the choices are kept and what they start as; the public demo keeps its own (`talkos.demo.`),
 * so trying it never changes the real app's look or language on the same browser. */
export interface PrefsOptions {
  /** Prefix of the storage keys. */
  storagePrefix?: string;
  /** A language to start in, over the stored one (the demo's `?lang=en`); otherwise the stored choice, or
   * Lithuanian. */
  initialLocale?: Locale;
}

const PrefsContext = createContext<Prefs | null>(null);

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch {
    // Storage blocked: the choice lasts until the tab closes.
  }
}

export function PrefsProvider({ children, storagePrefix = 'talkos.', initialLocale }: { children: ReactNode } & PrefsOptions) {
  const themeKey = `${storagePrefix}theme`;
  const localeKey = `${storagePrefix}locale`;
  const [theme, setThemeState] = useState<ThemeMode>(() => {
    const v = read(themeKey);
    return v === 'light' || v === 'dark' ? v : 'system';
  });
  // Lithuanian until the person picks something else, the browser's own language included: the
  // product and its customers are Lithuanian first.
  const [localeChoice, setLocaleState] = useState<LocaleChoice>(() => {
    if (initialLocale) return initialLocale;
    const v = read(localeKey);
    return v === 'lt' || v === 'en' || v === 'system' ? v : 'lt';
  });

  useEffect(() => {
    if (theme === 'system') delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = theme;
  }, [theme]);

  const locale = localeChoice === 'system' ? systemLocale() : localeChoice;
  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setTheme = useCallback(
    (t: ThemeMode) => {
      write(themeKey, t === 'system' ? null : t);
      setThemeState(t);
    },
    [themeKey],
  );
  const setLocale = useCallback(
    (l: LocaleChoice) => {
      // "Kaip sistemoje" is a choice too, kept as one: without a choice the app speaks Lithuanian.
      write(localeKey, l);
      setLocaleState(l);
    },
    [localeKey],
  );

  const value = useMemo(
    () => ({ theme, localeChoice, locale, setTheme, setLocale }),
    [theme, localeChoice, locale, setTheme, setLocale],
  );
  return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>;
}

export function usePrefs(): Prefs {
  const p = useContext(PrefsContext);
  if (!p) throw new Error('usePrefs outside PrefsProvider');
  return p;
}
